<template>
  <div class="helper-exchange-page">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>帮扶分兑换记录</span>
          <el-tag type="info">V4.0 帮扶分兑换解债分</el-tag>
        </div>
      </template>

      <!-- 统计卡片 -->
      <el-row :gutter="20" class="stats-row">
        <el-col :span="6">
          <el-statistic title="总兑换次数" :value="stats.totalCount" />
        </el-col>
        <el-col :span="6">
          <el-statistic title="总兑换帮扶分" :value="stats.totalAmount" :precision="2" />
        </el-col>
        <el-col :span="6">
          <el-statistic title="今日兑换" :value="stats.todayCount" />
        </el-col>
        <el-col :span="6">
          <el-statistic title="今日兑换金额" :value="stats.todayAmount" :precision="2" />
        </el-col>
      </el-row>

      <!-- 搜索栏 -->
      <el-form :inline="true" :model="searchForm" class="search-form">
        <el-form-item label="用户ID">
          <el-input v-model="searchForm.userId" placeholder="请输入用户ID" clearable style="width: 120px" />
        </el-form-item>
        <el-form-item label="手机号">
          <el-input v-model="searchForm.mobile" placeholder="请输入手机号" clearable style="width: 150px" />
        </el-form-item>
        <el-form-item label="时间范围">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="YYYY-MM-DD"
            style="width: 240px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">搜索</el-button>
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="success" @click="handleExport">导出</el-button>
        </el-form-item>
      </el-form>

      <!-- 数据表格 -->
      <el-table :data="tableData" v-loading="loading" border stripe>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="userId" label="用户ID" width="100" />
        <el-table-column prop="userName" label="用户名" width="120" />
        <el-table-column prop="mobile" label="手机号" width="130" />
        <el-table-column prop="amount" label="兑换帮扶分" width="120">
          <template #default="{ row }">
            <span class="amount">{{ row.amount?.toFixed(2) || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="exchangeRate" label="兑换比例" width="100">
          <template #default>
            <el-tag type="success">1:1</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="receivedAmount" label="获得解债分" width="120">
          <template #default="{ row }">
            <span class="received">+{{ row.amount?.toFixed(2) || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="balanceAfter" label="帮扶分余额" width="120">
          <template #default="{ row }">
            <span class="balance">{{ row.balanceAfter?.toFixed(2) || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80">
          <template #default>
            <el-tag type="success">成功</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="兑换时间" width="180">
          <template #default="{ row }">
            {{ formatTime(row.createdAt) }}
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        v-model:current-page="pagination.page"
        v-model:page-size="pagination.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        class="pagination"
      />
    </el-card>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

// 搜索表单
const searchForm = reactive({
  userId: '',
  mobile: '',
  dateRange: []
})

// 分页
const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

// 统计数据
const stats = reactive({
  totalCount: 0,
  totalAmount: 0,
  todayCount: 0,
  todayAmount: 0
})

// 表格数据
const tableData = ref([])
const loading = ref(false)

// 获取认证头
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin-token') || ''
  return {
    'Authorization': `Bearer ${token}`
  }
}

// 加载数据
const loadData = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.page,
      limit: pagination.pageSize
    }
    
    if (searchForm.userId) {
      params.userId = searchForm.userId
    }
    if (searchForm.mobile) {
      params.mobile = searchForm.mobile
    }
    if (searchForm.dateRange && searchForm.dateRange.length === 2) {
      params.startDate = searchForm.dateRange[0]
      params.endDate = searchForm.dateRange[1]
    }
    
    const response = await axios.get('/api/admin/helper-exchange/records', {
      params,
      headers: getAuthHeaders()
    })
    
    if (response.data.code === 200) {
      tableData.value = response.data.data.list || []
      pagination.total = response.data.data.total || 0
    } else {
      ElMessage.error(response.data.message || '加载失败')
    }
  } catch (error) {
    console.error('加载数据失败:', error)
    // 使用模拟数据
    tableData.value = []
    pagination.total = 0
  } finally {
    loading.value = false
  }
}

// 加载统计数据
const loadStats = async () => {
  try {
    const response = await axios.get('/api/admin/helper-exchange/stats', {
      headers: getAuthHeaders()
    })
    if (response.data.code === 200) {
      Object.assign(stats, response.data.data)
    }
  } catch (error) {
    console.error('加载统计失败:', error)
  }
}

// 搜索
const handleSearch = () => {
  pagination.page = 1
  loadData()
}

// 重置搜索
const resetSearch = () => {
  searchForm.userId = ''
  searchForm.mobile = ''
  searchForm.dateRange = []
  pagination.page = 1
  loadData()
}

// 导出
const handleExport = () => {
  ElMessage.info('导出功能开发中...')
}

// 分页
const handleSizeChange = (size) => {
  pagination.pageSize = size
  loadData()
}

const handleCurrentChange = (page) => {
  pagination.page = page
  loadData()
}

// 格式化时间
const formatTime = (time) => {
  if (!time) return '-'
  const date = new Date(time)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  })
}

onMounted(() => {
  loadData()
  loadStats()
})
</script>

<style scoped>
.helper-exchange-page {
  padding: 0;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stats-row {
  margin-bottom: 20px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 8px;
}

.search-form {
  margin-bottom: 20px;
}

.pagination {
  margin-top: 20px;
  justify-content: flex-end;
}

.amount {
  font-weight: bold;
  color: #e6a23c;
}

.received {
  font-weight: bold;
  color: #67c23a;
}

.balance {
  color: #409eff;
}

.positive {
  color: #67c23a;
}

.negative {
  color: #f56c6c;
}
</style>
